import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { useEffect, useContext, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLastSelectionText,
  getLanguageCode,
  getLastAssignmentVariable,
  getSubmodelIdFromSectionId,
  onRevertAssignment,
  getConfigureRequest,
  checkPasswordFeatureInConflict,
  getLastAssignmentInfo,
  getVariableSalesText,
} from '../services';
import { AppDispatch, AppState } from '../store';
import { ConflictTable } from './ConflictTable';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import { AppAuthContext } from '../services/Contexts';
import DraggableComponent from './DraggableComponent';
import { clearConflict, configure } from '../store/states/ConfigurationSlice';
import type {
  IConfigurationValue,
  IConfigurationVariable,
  IConflictDataPresentationType,
  IConflictDataSectionType,
  IConflictDataStoreType,
  IConflictDialogConflictType,
  IAutoSelectedAssignmentsDialogProps,
  IConflictHierarchy,
  IExtendedConfigureResponse,
  IModelContext,
  IModelContextHierarchy,
  INamedItem,
  ISection,
  ISubModelInfo,
  IValueType,
  IVariableAssignment,
} from '../../types';

/**
 * To initialize dataStore required for conflict table
 * @param {string} desc the model path description
 * @param {IConflictStoreType} dataStore the structure to store data
 * @param {INamed[]} pModelPath the array representing the model paths
 * @param {IModelContextHierarchy} node the hierarchy of models
 * @param {IModelContext} modelContext the model context
 * @returns {void}
 */
function initDataStore(
  desc: string,
  dataStore: IConflictDataStoreType,
  pModelPath: INamedItem[],
  shortSalesText: Map<string, string>,
  node?: IModelContextHierarchy,
  modelContext?: IModelContext
) {
  if (!node || !modelContext) {
    return;
  }
  for (const child of node?.children || []) {
    const $name =
      getVariableSalesText(shortSalesText, child.id) ??
      modelContext.subModels.find((subModel) => subModel.id === child.id)?.name ??
      '';
    const $desc = `${desc} > ${$name}`;
    const $modelPath = [...pModelPath, { id: child.id, name: $name }];
    dataStore[child.id] = {
      id: child.id,
      name: $name,
      desc: $desc,
      isModelConflict: true,
      modelPath: $modelPath,
      sections: {},
      conflicts: {},
    };
    initDataStore($desc, dataStore, $modelPath, shortSalesText, child, modelContext);
  }
}

/**
 * To segregate conflicts into the models
 * @param {IConflictHierarchy} modelConflicts the conflicts that have occured
 * @param {string} modelId the modelId
 * @param {IConflictDataStoreType} dataStore the structure to segregate conflicts in
 * @returns {void}
 */
function addConflictsToModels(
  modelConflicts: IConflictHierarchy,
  modelId: string,
  dataStore: IConflictDataStoreType
) {
  if (!modelConflicts || !Object.keys(modelConflicts).length) {
    return;
  }
  if (modelConflicts.conflicts) {
    const $conflicts: { [key: string]: IConflictDialogConflictType } = {};
    modelConflicts.conflicts.forEach((conflict) => {
      if (!(conflict.variable.id in $conflicts)) {
        $conflicts[conflict.variable.id] = {
          id: conflict.variable.id,
          name: conflict.variable.name,
          values: [],
          shortSalesText: conflict.variable.shortSalesText,
        };
      }
      $conflicts[conflict.variable.id].values.push(conflict.value);
      dataStore[modelId].conflicts = $conflicts;
    });
  }
  Object.keys(modelConflicts).forEach((key: string) => {
    if (key !== 'conflicts') {
      addConflictsToModels(modelConflicts[key] as IConflictHierarchy, key, dataStore);
    }
  });
}

/**
 * To check and add conflicts to the section if there is a conflict
 * @param {IConflictDataStoreType} dataStore the structure to segregate conflicts on
 * @param {string} modelId the modelId
 * @param {IConfigurationVariable[]} variables the variables in the section
 * @param {IConflictDataSectionType} section the section to store data
 * @returns {boolean} whether to add section has conflicts or not
 */
function getSectionConflicts(
  dataStore: IConflictDataStoreType,
  modelId: string,
  variables: IConfigurationVariable[],
  section: IConflictDataSectionType
): boolean {
  let flg = false;
  if (dataStore[modelId]?.conflicts) {
    for (const variable of variables) {
      if (variable.id in dataStore[modelId].conflicts) {
        flg = true;
        section.conflicts.push(dataStore[modelId].conflicts[variable.id]);
      } else {
        dataStore[modelId].isModelConflict = false;
      }
    }
  }
  return flg;
}

/**
 * To segregate conflicts into their respective sections
 * @param {IConflictDataStoreType} dataStore the structure to segregate conflicts on
 * @param {string} modelId the modelId
 * @param {ISection} section the section
 * @returns {void}
 */
function addConflictsToSections(
  dataStore: IConflictDataStoreType,
  modelId: string,
  section: ISection
) {
  if (!dataStore || !modelId || !section) {
    return;
  }
  const $modelId = getSubmodelIdFromSectionId(section?.id || '');
  const variables = section?.variables || [];
  const sections = section?.sections || [];
  if ($modelId) {
    modelId = $modelId;
  }
  const $section: IConflictDataSectionType = {
    id: section.id,
    name: section.name ?? '',
    conflicts: [],
  };
  const flg = getSectionConflicts(dataStore, modelId, variables, $section);
  if (flg) {
    dataStore[modelId].sections[section.id] = $section;
  }

  for (const sec of sections) {
    addConflictsToSections(dataStore, modelId, sec);
  }
}

/**
 * To get the final structure required for representing the conflict table
 * @param {ISubModelInfo} rootModel the root model data
 * @param {IModelContext} modelContext the model context
 * @param {IConflictHierarchy} conflicts the conflicts that occcured
 * @param {ISection[]} rootSections the sections as received in response
 * @returns {IConflictDataPresentationType[]} the structure required for representing conflicts
 */
function getTableRows(
  rootModel: ISubModelInfo,
  modelContext: IModelContext,
  conflicts: IConflictHierarchy,
  rootSections: ISection[],
  shortSalesText: Map<string, string>
): IConflictDataPresentationType[] {
  const rootModelName = getVariableSalesText(shortSalesText, rootModel.id) ?? rootModel.name;
  const dataStore: IConflictDataStoreType = {
    [rootModel.id]: {
      id: rootModel.id,
      name: rootModelName,
      desc: rootModelName,
      isModelConflict: true,
      modelPath: [{ id: rootModel.id, name: rootModelName }],
      sections: {},
      conflicts: {},
    },
  };
  initDataStore(
    rootModelName,
    dataStore,
    [{ id: rootModel.id, name: rootModelName }],
    shortSalesText,
    modelContext?.hierarchy,
    modelContext
  );
  addConflictsToModels(conflicts, rootModel.id, dataStore);
  for (const sec of rootSections) {
    addConflictsToSections(dataStore, rootModel.id, sec);
  }
  return Object.values(dataStore)
    .filter((model) => Object.keys(model?.conflicts || {}).length)
    ?.map((model) => ({ ...model, sections: Object.values(model.sections) }));
}

function IsLastAssignmentInConflict(
  lastAssignmentVariable: IConfigurationVariable | null,
  lastAssignmentValue: IConfigurationValue | IValueType,
  removedAssignments?: IVariableAssignment[]
) {
  if (!removedAssignments?.length || !lastAssignmentValue || !lastAssignmentVariable) {
    return false;
  }
  let lastValue: { value?: IValueType };
  if (typeof lastAssignmentValue === 'object') {
    lastValue = lastAssignmentValue;
  } else {
    lastValue = { value: lastAssignmentValue };
  }
  return removedAssignments?.some((v: IVariableAssignment) => {
    return (
      lastAssignmentVariable.id === v.variable.id && lastValue?.value === v.value.value!.toString()
    );
  });
}

//Revert the last assignment When User selects Cancel
const onRevert = (
  configuration: IExtendedConfigureResponse,
  token: string,
  dispatch: AppDispatch
) => {
  const request = onRevertAssignment(configuration);
  if (request) {
    dispatch(configure({ request: request, token: token, language: getLanguageCode() }));
  }
};

//Keeps the selection and removes the conflicted assignment

const keepSelection = (
  configuration: IExtendedConfigureResponse,
  token: string,
  dispatch: AppDispatch
) => {
  const request = getConfigureRequest(configuration, true);
  dispatch(configure({ request: request, token: token, language: getLanguageCode() }));
  dispatch(clearConflict());
};

/**
 * Renders the conflict dialog when the user selects an assignments which is causing a conflict
 * @returns {JSX.Element} the conflict dialog
 */
export const ConflictDialog = ({
  assignedByRuleDifferences,
  onAccept,
}: IAutoSelectedAssignmentsDialogProps) => {
  const { t } = useTranslation();
  const token: string = useContext(AppAuthContext);

  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const configuration = useSelector((state: AppState) => state.configuration);
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);

  const dispatch = useDispatch<AppDispatch>();
  const [conflictsState, setConflictsState] = useState<IConflictDataPresentationType[]>([]);
  const conflicts = configuration.conflict;
  const rootSections = configuration.data?.sections || [];
  const modelContext = configuration.savedConfiguration?.modelContext;
  const rootModel = configuration.savedConfiguration?.modelContext.rootModel;
  let isLastAssignmentInConflict: boolean | null = null;

  //check any  removed assignments variable has isPassword feature
  const passwordFeatureConflict: IVariableAssignment | undefined =
    checkPasswordFeatureInConflict(configuration);

  const handleKeepSelection = useCallback(() => {
    // Call the callback to update assignedByRuleAtState in Configurator
    if (onAccept && assignedByRuleDifferences?.differences) {
      onAccept(assignedByRuleDifferences.differences);
    }
    keepSelection(configuration, token, dispatch);
  }, [configuration, assignedByRuleDifferences]);

  useEffect(() => {
    if (!isLastAssignmentInConflict && conflicts && rootModel) {
      const $assignmentsToRemove = conflicts?.assignmentsToRemove || ({} as IConflictHierarchy);
      setConflictsState(
        getTableRows(
          rootModel,
          modelContext,
          $assignmentsToRemove,
          rootSections,
          configuration.shortSalesText
        )
      );
    }
  }, [conflicts?.assignmentsToRemove, isLastAssignmentInConflict]);

  if (
    !configuration.conflict ||
    !configuration.lastChange ||
    !configuration.data?.removedAssignments
  ) {
    return null;
  }

  const lastAssignmentVariable = getLastAssignmentVariable(configuration);
  const lastAssignmentValue = getLastAssignmentInfo(configuration);

  //Check if last assigment is also your removed assignment
  isLastAssignmentInConflict = IsLastAssignmentInConflict(
    lastAssignmentVariable,
    lastAssignmentValue,
    configuration.data.removedAssignments?.variableAssignments
  );

  const classSelector = Number(applicationSettings?.pageSize?.isExtraSmall);
  const flexDirection = ['flex-row', 'flex-column'];
  const alignSelf = ['alignself-center', 'alignself-start'];
  const maxWidth = ['mw-25', 'mw-100'];
  const toolTipModifier = [
    {
      name: 'offset',
      options: {
        offset: [-10, 0], // Adjust the first value to move left/right
      },
    },
  ];
  return isLastAssignmentInConflict || passwordFeatureConflict ? (
    <Dialog
      className="conflictDialog-root"
      open
      aria-describedby="alert-dialog-description"
      aria-labelledby="draggable-dialog-title"
      PaperComponent={DraggableComponent}
    >
      <DialogTitle className="header d-flex align-center cursor-move h-auto">
        {t('conflictDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box className="conflictError">
          <CancelIcon className="btn-decrement cancelIcon" />
          <span>
            {passwordFeatureConflict
              ? t('conflictDialog.passwordConflictError', {
                  name: passwordFeatureConflict.variable.name,
                  id: passwordFeatureConflict.variable.id,
                })
              : t('conflictDialog.otherConflictError')}
          </span>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          className="text-capitalize"
          color="info"
          variant="contained"
          onClick={() => onRevert(configuration, token, dispatch)}
        >
          {t('button.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <Dialog
      className="conflictDialog-root"
      open
      slotProps={{ paper: { className: 'conflictDialog-paper' } }}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle className="header d-flex align-center cursor-move h-auto">
        {t('conflictDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box className={`selection ${flexDirection[classSelector]}`}>
          <Box className={`selectionLabel ${alignSelf[classSelector]} ${maxWidth[classSelector]}`}>
            {`${t('labels.selection')}`}
          </Box>
          <Box className="selectionValue">
            {lastAssignmentVariable &&
              getLastSelectionText(lastAssignmentVariable, lastAssignmentValue, showCode, true)}
          </Box>
        </Box>
        <ConflictTable
          modelRows={conflictsState}
          showCode={showCode}
          assignedByRuleDifferences={assignedByRuleDifferences}
        />
      </DialogContent>
      <DialogActions className="footer">
        <Tooltip
          title={t('tooltip.keepSelection')}
          placement="top"
          arrow
          slotProps={{
            popper: {
              modifiers: toolTipModifier,
              style: { maxWidth: '120px' },
            },
          }}
        >
          <Button
            className="text-capitalize"
            color="info"
            variant="contained"
            onClick={handleKeepSelection}
          >
            {t('button.keepSelection')}
          </Button>
        </Tooltip>
        <Button
          className="text-capitalize"
          color="primary"
          variant="contained"
          onClick={() => onRevert(configuration, token, dispatch)}
        >
          {t('button.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
